/**
 * GED ESS - Architecture Documentaire Cooperative
 * Conforme aux principes ESS : Transparence, tracabilite, gestion desinteressee
 *
 * Entites : OSO, FOGEDOM, COPPAM, FTPE, KDMARCHE, PRIMACOP
 */
import React from 'react';
import { Upload, FileText, Download, X, CheckCircle, XCircle, Clock, Shield, Users, Scale, AlertTriangle, ChevronDown, ChevronRight, Eye, Grid3X3, List, Plus, Settings, Printer } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { cn } from '../../../lib/utils';
import { WORKFLOW_RULES, canPreviewInline } from './constants';
import DocumentPreviewModal from './DocumentPreviewModal';
import EntityCard from './EntityCard';
import DocumentDisplay from './DocumentDisplay';
import AdvancedSearchBar from './AdvancedSearchBar';
import CategoryManager from './CategoryManager';
import useGEDESS from './useGEDESS';
import GEDESSPanels from './GEDESSPanels';
import GEDESSUploadModal from './GEDESSUploadModal';

export default function GEDESS({ userRole = 'member' }) {
  const ctx = useGEDESS(userRole);
  const {
    error, setError, success,
    allEntities, documentsByEntity, setSelectedEntity, selectedEntity,
    showAddEntity, setShowAddEntity, newEntityForm, setNewEntityForm, handleCreateEntity,
    souscriptionDocuments, showSouscriptionDocs, setShowSouscriptionDocs, handleView, handlePrint, handleDownload,
    documents, connectedCounts, complianceData, auditEntries, activePanel, setActivePanel,
    isDragging, handleDragOver, handleDragLeave, handleDrop, setDragFiles, setDragPreviews, setUploadForm, setShowUploadModal, uploadForm,
    currentEntity, showCategoryManager, setShowCategoryManager, viewMode, setViewMode,
    customCategories, loadCustomCategories,
    searchQuery, setSearchQuery, docFilters, setDocFilters, getMergedCategories, filteredDocuments,
    handleDelete, loading, formatFileSize, formatDate, getStatusBadge, handlePublishToRegistry, publishingDoc, printingDocId,
    previewDoc, setPreviewDoc,
  } = ctx;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-6 py-6">
        <h1 className="text-xl font-bold">GED ESS – Architecture documentaire cooperative</h1>
        <p className="text-gray-400 mt-1 text-sm">Gestion electronique des documents – Conformite ESS, tracabilite, cloisonnement juridique</p>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center justify-between text-sm">
            <div className="flex items-center gap-2"><XCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span></div>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setError('')}><X className="w-3 h-3" /></Button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2 text-sm">
            <CheckCircle className="w-4 h-4 flex-shrink-0" /><span>{success}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Column - Entities */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Entites ESS</h2>
              <Button
                onClick={() => { setUploadForm({ ...uploadForm, entite: selectedEntity }); setShowUploadModal(true); }}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 h-8 text-xs"
                data-testid="upload-btn"
              >
                <Upload className="w-3.5 h-3.5 mr-1" />
                Ajouter
              </Button>
            </div>

            <div className="space-y-2">
              {allEntities.map(entity => (
                <EntityCard
                  key={entity.id}
                  entity={entity}
                  documentsCount={documentsByEntity[entity.id]}
                  onSelect={setSelectedEntity}
                  isSelected={selectedEntity === entity.id}
                />
              ))}

              {/* Add Entity Button (Super Admin) */}
              {userRole === 'super_admin' && (
                <div className="pt-1">
                  {showAddEntity ? (
                    <div className="bg-white rounded-lg border border-dashed border-amber-300 p-3 space-y-2" data-testid="add-entity-form">
                      <Input
                        value={newEntityForm.name}
                        onChange={(e) => setNewEntityForm(f => ({ ...f, name: e.target.value }))}
                        placeholder="Nom de l'entite (ex: COOPBAT)"
                        className="h-8 text-sm"
                        data-testid="new-entity-name"
                      />
                      <Input
                        value={newEntityForm.description}
                        onChange={(e) => setNewEntityForm(f => ({ ...f, description: e.target.value }))}
                        placeholder="Description courte"
                        className="h-8 text-sm"
                        data-testid="new-entity-description"
                      />
                      <select
                        value={newEntityForm.badge_color}
                        onChange={(e) => setNewEntityForm(f => ({ ...f, badge_color: e.target.value }))}
                        className="w-full h-8 text-xs border rounded-md px-2"
                        data-testid="new-entity-color"
                      >
                        <option value="bg-cyan-600">Cyan</option>
                        <option value="bg-teal-600">Teal</option>
                        <option value="bg-indigo-600">Indigo</option>
                        <option value="bg-pink-600">Rose</option>
                        <option value="bg-lime-600">Vert clair</option>
                        <option value="bg-sky-600">Bleu ciel</option>
                        <option value="bg-stone-600">Pierre</option>
                      </select>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={() => setShowAddEntity(false)}>Annuler</Button>
                        <Button size="sm" className="flex-1 h-7 text-xs bg-amber-600 hover:bg-amber-700" onClick={handleCreateEntity} data-testid="confirm-add-entity">Creer</Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAddEntity(true)}
                      className="w-full flex items-center gap-2 p-2.5 rounded-lg border border-dashed border-gray-300 text-gray-500 hover:border-amber-400 hover:text-amber-600 transition-colors text-sm"
                      data-testid="add-entity-btn"
                    >
                      <Plus className="w-4 h-4" /> Ajouter une entite ESS
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Souscription Documents */}
            {souscriptionDocuments.length > 0 && (
              <Card className="border-blue-200 bg-blue-50/30">
                <CardHeader className="pb-2 px-3 pt-3">
                  <button onClick={() => setShowSouscriptionDocs(!showSouscriptionDocs)} className="flex items-center justify-between w-full">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      Documents Souscription
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-600 text-white text-[10px] font-semibold">{souscriptionDocuments.length}</Badge>
                      {showSouscriptionDocs ? <ChevronDown className="w-3 h-3 text-blue-600" /> : <ChevronRight className="w-3 h-3 text-blue-600" />}
                    </div>
                  </button>
                </CardHeader>
                {showSouscriptionDocs && (
                  <CardContent className="pt-0 px-3 pb-3">
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {souscriptionDocuments.map(doc => (
                        <div key={doc.id} className="p-2 bg-white rounded-lg border border-blue-100 hover:border-blue-300 transition-colors">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <Badge variant="outline" className={cn("text-[10px] px-1 py-0", doc.type === 'devis_ess' ? 'border-blue-400 text-blue-600' : 'border-emerald-400 text-emerald-600')}>
                                  {doc.type === 'devis_ess' ? 'Devis' : 'Contrat'}
                                </Badge>
                              </div>
                              <p className="font-medium text-xs text-gray-900 truncate mt-1">{doc.nom}</p>
                            </div>
                            <div className="flex gap-0.5 flex-shrink-0">
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-blue-600" onClick={() => handleView(doc)} title="Voir">
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-600" onClick={() => handlePrint(doc)} title="Imprimer" data-testid={`print-doc-${doc.id}`}>
                                <Printer className="w-3.5 h-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={() => handleDownload(doc)} title="Telecharger">
                                <Download className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Workflow Rules */}
            <Card className="border-l-[5px] border-l-amber-500 bg-amber-50/30">
              <CardHeader className="pb-2 px-3 pt-3">
                <Badge className="bg-gray-900 text-white text-[10px] w-fit font-semibold">WORKFLOW ESS</Badge>
                <CardTitle className="text-sm mt-1">Regle documentaire</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-3 pb-3">
                <ul className="space-y-1">
                  {WORKFLOW_RULES.map((rule, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-gray-600">
                      <CheckCircle className="w-3 h-3 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Documents + Connected Sources */}
          <div className="lg:col-span-3 space-y-4">
            {/* Navigation Tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto" data-testid="panel-tabs">
              {[
                { id: 'documents', label: 'Documents', icon: FileText, count: documents.length },
                { id: 'membres', label: 'Membres', icon: Users, count: connectedCounts.membres || 0 },
                { id: 'ftpe', label: 'FTPE', icon: Shield, count: connectedCounts.ftpe_adhesions || 0 },
                { id: 'simulations', label: 'Simulations RCDS', icon: Scale, count: connectedCounts.simulations_rcds || 0 },
                { id: 'avenants', label: 'Avenants', icon: FileText, count: connectedCounts.avenants || 0 },
                { id: 'conformite', label: 'Conformite', icon: CheckCircle, count: complianceData?.checks?.filter(c => c.status === 'conforme').length || 0 },
                { id: 'journal', label: 'Journal audit', icon: Clock, count: auditEntries.length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActivePanel(tab.id)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium whitespace-nowrap transition-colors",
                    activePanel === tab.id ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
                  )}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  {tab.label}
                  <Badge className={cn("text-[10px] px-1.5 py-0", activePanel === tab.id ? "bg-amber-600 text-white" : "bg-gray-200 text-gray-600")}>{tab.count}</Badge>
                </button>
              ))}
            </div>

            {/* Drag & Drop Zone */}
            <div
              className={cn(
                "border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer",
                isDragging ? "border-amber-500 bg-amber-50 scale-[1.01]" : "border-gray-300 hover:border-amber-400 hover:bg-amber-50/30"
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => { setDragFiles([]); setDragPreviews([]); setUploadForm(prev => ({ ...prev, file: null, entite: selectedEntity })); setShowUploadModal(true); }}
              data-testid="drop-zone"
            >
              <Upload className={cn("w-8 h-8 mx-auto mb-2", isDragging ? "text-amber-600" : "text-gray-400")} />
              <p className="text-sm font-medium text-gray-700">
                {isDragging ? "Deposez vos fichiers ici" : "Glissez-deposez vos documents ici"}
              </p>
              <p className="text-xs text-gray-500 mt-1">ou cliquez pour parcourir (PDF, Images, Word, Excel)</p>
            </div>

            {/* PANEL: Documents */}
            {activePanel === 'documents' && currentEntity && (
              <Card className="overflow-hidden">
                <CardHeader className="border-b bg-white pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", currentEntity.badgeColor)}>
                        <currentEntity.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <Badge className={cn("text-[10px] text-white mb-0.5", currentEntity.badgeColor)}>{currentEntity.badge}</Badge>
                        <CardTitle className="text-base">{currentEntity.fullName}</CardTitle>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(userRole === 'super_admin' || userRole === 'admin') && (
                        <button
                          onClick={() => setShowCategoryManager(!showCategoryManager)}
                          className={cn("p-1.5 rounded transition-colors", showCategoryManager ? 'bg-amber-100 text-amber-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100')}
                          title="Gerer les categories"
                          data-testid="toggle-category-manager"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      )}
                      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5" data-testid="view-mode-toggle">
                        <button onClick={() => setViewMode('list')} className={cn("p-1.5 rounded", viewMode === 'list' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700')} title="Vue liste" data-testid="view-mode-list"><List className="w-4 h-4" /></button>
                        <button onClick={() => setViewMode('grid')} className={cn("p-1.5 rounded", viewMode === 'grid' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700')} title="Vue grille" data-testid="view-mode-grid"><Grid3X3 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                  {currentEntity.notice && (
                    <div className="mt-3 p-3 border border-dashed border-amber-400 bg-amber-50/50 rounded-lg">
                      <div className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /><p className="text-xs text-gray-700">{currentEntity.notice}</p></div>
                    </div>
                  )}

                  {/* Category Manager (Super Admin) */}
                  {showCategoryManager && (
                    <div className="mt-3">
                      <CategoryManager
                        entityId={selectedEntity}
                        entityName={currentEntity.name}
                        defaultCategories={currentEntity.categories}
                        customCategories={customCategories[selectedEntity] || []}
                        onCategoriesChange={loadCustomCategories}
                        badgeColor={currentEntity.badgeColor}
                      />
                    </div>
                  )}

                  {/* Advanced Search */}
                  <div className="mt-3">
                    <AdvancedSearchBar
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      filters={docFilters}
                      setFilters={setDocFilters}
                      categories={getMergedCategories(selectedEntity)}
                      entityColor={currentEntity.badgeColor}
                      placeholder="Rechercher par nom, categorie, auteur, description..."
                    />
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-xs text-gray-500">{filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} {(searchQuery || Object.values(docFilters).some(v => v)) ? '(filtre)' : ''}</div>
                    {Object.values(docFilters).some(v => v) && (
                      <div className="flex flex-wrap gap-1">
                        {docFilters.categorie && <Badge variant="outline" className="text-[10px] gap-1">{docFilters.categorie} <button onClick={() => setDocFilters(f => ({...f, categorie: ''}))}><X className="w-2.5 h-2.5" /></button></Badge>}
                        {docFilters.statut && <Badge variant="outline" className="text-[10px] gap-1">{docFilters.statut} <button onClick={() => setDocFilters(f => ({...f, statut: ''}))}><X className="w-2.5 h-2.5" /></button></Badge>}
                        {docFilters.type_fichier && <Badge variant="outline" className="text-[10px] gap-1">{docFilters.type_fichier} <button onClick={() => setDocFilters(f => ({...f, type_fichier: ''}))}><X className="w-2.5 h-2.5" /></button></Badge>}
                        {docFilters.periode && <Badge variant="outline" className="text-[10px] gap-1">{docFilters.periode}j <button onClick={() => setDocFilters(f => ({...f, periode: ''}))}><X className="w-2.5 h-2.5" /></button></Badge>}
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <DocumentDisplay documents={filteredDocuments} categories={getMergedCategories(selectedEntity)} onView={handleView} onDownload={handleDownload} onPrint={handlePrint} onDelete={handleDelete} loading={loading} formatFileSize={formatFileSize} formatDate={formatDate} getStatusBadge={getStatusBadge} viewMode={viewMode} onPublish={userRole === 'super_admin' ? handlePublishToRegistry : null} publishingDoc={publishingDoc} printingDocId={printingDocId} />
                </CardContent>
              </Card>
            )}

            {/* Connected-source panels */}
            <GEDESSPanels {...ctx} />
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
          onDownload={handleDownload}
          documents={filteredDocuments.filter(d => canPreviewInline(d.original_filename || d.filename, d.mime_type))}
          onNavigate={(nextDoc) => setPreviewDoc(nextDoc)}
        />
      )}

      {/* Upload Modal with Drag & Drop Preview */}
      <GEDESSUploadModal {...ctx} />
    </div>
  );
}
